import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, X, ChevronDown, ChevronRight, Upload, Download, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "pendiente", label: "Pendiente", color: "#94a3b8" },
  { value: "en_preparacion", label: "En preparación", color: "#60a5fa" },
  { value: "en_revision", label: "En revisión", color: "#22d3ee" },
  { value: "observado", label: "Observado", color: "#fbbf24" },
  { value: "validado", label: "Validado", color: "#4ade80" },
  { value: "rechazado", label: "Rechazado", color: "#f87171" },
  { value: "no_aplica", label: "No aplica", color: "#64748b" },
];
const stMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

function ItemRow({ cl, item, canManage, onUpdated, corporateDocs }) {
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/requirements/checklists/${cl.id}/items/${item.number}`, body);
      onUpdated(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al actualizar"); }
    finally { setBusy(false); }
  };

  const changeStatus = (status) => {
    if (status === "no_aplica") {
      const justification = prompt("Justificación de 'No aplica' (obligatoria):", item.justification || "");
      if (!justification?.trim()) {
        toast.error("'No aplica' requiere una justificación — no se aplicó el cambio");
        return;
      }
      return patch({ status, justification });
    }
    patch({ status });
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/requirements/checklists/${cl.id}/items/${item.number}/document`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento adjuntado (pendiente de validación)");
      const { data } = await api.get(`/requirements/checklists/${cl.id}`);
      onUpdated(data);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo subir"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const downloadDoc = async () => {
    try {
      const res = await api.get(`/requirements/checklists/${cl.id}/items/${item.number}/document`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: item.document?.content_type })), "_blank");
    } catch { toast.error("No se pudo descargar"); }
  };

  const meta = stMeta(item.status);
  return (
    <div className="border-b py-2.5" style={{ borderColor: "var(--border)" }} data-testid={`req-item-${cl.id}-${item.number}`}>
      {item.separator && <div className="text-[10px] font-mono text-amber-400 uppercase tracking-widest mb-1">➤ {item.separator}</div>}
      <div className="flex flex-wrap items-start gap-2">
        <span className="font-mono text-cyan-300 w-7 text-right shrink-0">{item.number}.</span>
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm text-slate-200">
            {item.name}
            {item.conditional && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm font-mono uppercase text-amber-300 bg-amber-400/10">Condicional</span>}
          </div>
          {item.description && <div className="text-xs text-slate-500">{item.description}</div>}
          {item.status === "no_aplica" && item.justification && (
            <div className="text-xs text-slate-400 italic mt-0.5">Justificación: {item.justification}</div>
          )}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {item.document && (
              <button onClick={downloadDoc} className="text-xs text-cyan-300 hover:underline flex items-center gap-1" data-testid={`req-doc-link-${cl.id}-${item.number}`}>
                <Download className="w-3 h-3" /> {item.document.name}
              </button>
            )}
            {item.corporate_document && (
              <span className="text-xs text-indigo-300 flex items-center gap-1">
                <Link2 className="w-3 h-3" /> {item.corporate_document.name} (v{item.corporate_document.version})
              </span>
            )}
            {item.observations && <span className="text-xs text-amber-200">Obs: {item.observations}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canManage ? (
            <select data-testid={`req-status-${cl.id}-${item.number}`} value={item.status} disabled={busy}
              onChange={(e) => changeStatus(e.target.value)}
              className="input-cyber text-xs py-1" style={{ color: meta.color }}>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase" style={{ color: meta.color, background: `${meta.color}1a` }}>{meta.label}</span>
          )}
          {canManage && (
            <>
              <label title="Adjuntar documento" className="cursor-pointer text-slate-400 hover:text-cyan-300">
                <Upload className="w-4 h-4" />
                <input type="file" hidden onChange={uploadDoc} disabled={busy} data-testid={`req-upload-${cl.id}-${item.number}`} />
              </label>
              <button title="Vincular documento corporativo" onClick={() => setLinking(!linking)}
                className="text-slate-400 hover:text-indigo-300" data-testid={`req-link-${cl.id}-${item.number}`}>
                <Link2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
      {linking && canManage && (
        <div className="mt-2 ml-9 flex items-center gap-2">
          <select className="input-cyber text-xs flex-1" defaultValue=""
            onChange={(e) => { if (e.target.value) { patch({ corporate_document_id: e.target.value }); setLinking(false); } }}
            data-testid={`req-link-select-${cl.id}-${item.number}`}>
            <option value="">— Seleccionar documento corporativo —</option>
            {corporateDocs.map((d) => <option key={d.id} value={d.id}>{d.name} (v{d.version})</option>)}
          </select>
          {item.corporate_document && (
            <button onClick={() => { patch({ unlink_corporate_document: true }); setLinking(false); }}
              className="text-xs text-red-300 hover:underline">Desvincular</button>
          )}
        </div>
      )}
      {canManage && (
        <div className="mt-1.5 ml-9 flex gap-2 flex-wrap">
          <input placeholder="Responsable" defaultValue={item.responsible || ""} className="input-cyber text-xs py-1 w-36"
            onBlur={(e) => { if (e.target.value !== (item.responsible || "")) patch({ responsible: e.target.value }); }}
            data-testid={`req-responsible-${cl.id}-${item.number}`} />
          <input type="date" defaultValue={item.due_date || ""} className="input-cyber text-xs py-1"
            onChange={(e) => patch({ due_date: e.target.value || null })} data-testid={`req-due-${cl.id}-${item.number}`} />
          <input placeholder="Observaciones" defaultValue={item.observations || ""} className="input-cyber text-xs py-1 flex-1 min-w-[160px]"
            onBlur={(e) => { if (e.target.value !== (item.observations || "")) patch({ observations: e.target.value }); }}
            data-testid={`req-obs-${cl.id}-${item.number}`} />
        </div>
      )}
    </div>
  );
}

export default function ContractChecklists({ contract }) {
  const { user } = useAuth();
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [corporateDocs, setCorporateDocs] = useState([]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tplId, setTplId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/contracts/${contract.id}/checklists`);
      setChecklists(data);
    } catch { /* sin permiso o error */ }
  };
  useEffect(() => {
    load();
    if (canManage) {
      api.get("/requirements/templates").then((r) => setTemplates(r.data.filter((t) => t.active))).catch(() => {});
      api.get("/legal/documents").then((r) => setCorporateDocs(r.data.filter((d) => d.status === "vigente"))).catch(() => {});
    }
    // eslint-disable-next-line
  }, [contract.id]);

  const create = async () => {
    if (!tplId) return toast.error("Selecciona una plantilla");
    setSaving(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/checklists`, { template_id: tplId });
      toast.success("Expediente de requisitos creado");
      setCreating(false);
      setTplId("");
      setChecklists((c) => [data, ...c]);
      setOpen(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear"); }
    finally { setSaving(false); }
  };

  const remove = async (cl) => {
    if (!confirm(`¿Eliminar el expediente "${cl.label}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/requirements/checklists/${cl.id}`);
      toast.success("Expediente eliminado");
      setChecklists((c) => c.filter((x) => x.id !== cl.id));
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
  };

  const onUpdated = (data) => setChecklists((c) => c.map((x) => (x.id === data.id ? data : x)));

  return (
    <div className="panel" data-testid="contract-checklists">
      <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <div className="font-display font-bold text-sm text-slate-200">Expedientes de Requisitos (Trámites)</div>
        {canManage && (
          <button data-testid="checklist-new-btn" onClick={() => setCreating(!creating)}
            className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5"
            style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
            <Plus className="w-3.5 h-3.5" /> Nuevo expediente
          </button>
        )}
      </div>
      <div className="p-4 space-y-3">
        {creating && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="checklist-create-form">
            <select value={tplId} onChange={(e) => setTplId(e.target.value)} className="input-cyber text-xs flex-1 min-w-[240px]" data-testid="checklist-template-select">
              <option value="">— Seleccionar plantilla de requisitos —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="checklist-create-btn" onClick={create} disabled={saving}
              className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Crear
            </button>
            <button onClick={() => setCreating(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
          </div>
        )}
        {checklists.length === 0 && (
          <div className="text-xs text-slate-500 italic">Sin expedientes de requisitos. Crea uno desde una plantilla (ej. IGIFE Anticipo o Estimación 1).</div>
        )}
        {checklists.map((cl) => (
          <div key={cl.id} className="rounded-sm" style={{ background: "rgba(15,23,42,0.5)", border: "1px solid var(--border)" }} data-testid={`checklist-${cl.id}`}>
            <div className="px-3 py-2.5 flex items-center justify-between gap-2 cursor-pointer" onClick={() => setOpen(open === cl.id ? null : cl.id)}>
              <div className="flex items-center gap-2 min-w-0">
                {open === cl.id ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-200 truncate">{cl.label}</div>
                  <div className="text-[10px] font-mono text-slate-500">{cl.progress.validados}/{cl.progress.aplicables} validados · {cl.progress.total - cl.progress.aplicables} no aplican</div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="w-28 h-2 rounded-full overflow-hidden" style={{ background: "rgba(148,163,184,0.15)" }}>
                  <div className="h-full rounded-full" style={{ width: `${cl.progress.pct}%`, background: cl.progress.pct >= 100 ? "#4ade80" : "var(--cyan)" }} />
                </div>
                <span className="font-mono text-xs text-cyan-300 tabular-nums" data-testid={`checklist-pct-${cl.id}`}>{cl.progress.pct}%</span>
                {user?.role === "admin" && (
                  <button onClick={(e) => { e.stopPropagation(); remove(cl); }} className="text-slate-500 hover:text-red-400" data-testid={`checklist-delete-${cl.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            {open === cl.id && (
              <div className="px-3 pb-3">
                {cl.items.map((it) => (
                  <ItemRow key={it.number} cl={cl} item={it} canManage={canManage} onUpdated={onUpdated} corporateDocs={corporateDocs} />
                ))}
                {cl.physical_requirements && (
                  <div className="mt-3 p-3 rounded-sm" style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)" }}>
                    <div className="label-mono mb-1" style={{ color: "#fbbf24" }}>// REQUISITOS FÍSICOS DEL TRÁMITE</div>
                    <div className="text-xs text-slate-300">{cl.physical_requirements}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
